import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-card border border-border rounded-lg p-8 shadow-card">
          <h1 className="font-display text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

          <div className="prose prose-sm max-w-none space-y-6 text-foreground">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">1. Introduction</h2>
              <p className="text-muted-foreground">
                La Olla Restaurant ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and reservation services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">2. Information We Collect</h2>
              <p className="text-muted-foreground mb-2">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Personal Information:</strong> Name, email address (if provided), hotel/cabin information</li>
                <li><strong>Reservation Details:</strong> Meal preferences, dates of stay, number of guests, dietary requirements</li>
                <li><strong>Payment Information:</strong> Payment method used, transaction details (processed securely through our payment providers)</li>
                <li><strong>Communication Data:</strong> Special requests, notes, and any correspondence with us</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-2">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Process and manage your meal reservations</li>
                <li>Prepare meals according to your dietary requirements</li>
                <li>Process payments and maintain billing records</li>
                <li>Communicate with you about your reservations</li>
                <li>Improve our services and customer experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">4. Information Sharing</h2>
              <p className="text-muted-foreground mb-2">
                We may share your information with:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Partner Hotels:</strong> To coordinate your stay and meal service</li>
                <li><strong>Payment Processors:</strong> To securely process your payments (Banco Nacional, PayPal)</li>
                <li><strong>Service Providers:</strong> Who assist us in operating our business</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">5. Data Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our website uses SSL encryption to secure data transmission. Payment information is processed through PCI-compliant payment providers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">6. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Reservation records are typically retained for 2 years for business and legal purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">7. Your Rights</h2>
              <p className="text-muted-foreground mb-2">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information (subject to legal requirements)</li>
                <li>Object to processing of your information</li>
                <li>Request data portability</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">8. Cookies and Tracking</h2>
              <p className="text-muted-foreground">
                Our website uses essential cookies to ensure proper functionality. We do not use tracking cookies or third-party analytics that collect personal information. Session data is stored temporarily to maintain your reservation process.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">9. Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our services are not directed to children under 18. We do not knowingly collect personal information from children. Reservations must be made by adults (18 years or older).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">10. International Data Transfers</h2>
              <p className="text-muted-foreground">
                Your information may be transferred to and processed in countries other than Costa Rica. We ensure appropriate safeguards are in place to protect your information in compliance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">11. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">12. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-2 text-muted-foreground">
                <p><strong className="text-foreground">La Olla Restaurant</strong></p>
                <p>Drake Bay, Osa Peninsula</p>
                <p>Puntarenas, Costa Rica</p>
                <p>Email: privacy@amayascorcovado.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
